// src/api/demoApi.ts

import { baseApi } from './baseApi';
import type { Workspace } from '../types/api';

// ============================================================
// Types
// ============================================================

export interface CreateDemoWorkspaceRequest {
  forceNew?: boolean;
}

export interface CreateDemoWorkspaceResponse {
  workspace: Pick<Workspace, 'id' | 'name' | 'slug' | 'type' | 'plan' | 'industry' | 'isDemo' | 'createdAt'>;
  message: string;
}

export interface DemoWorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  type: string;
  plan: string;
  industry: string | null;
  isDemo: boolean;
  createdAt: string;
}

export interface ListDemoWorkspacesResponse {
  workspaces: DemoWorkspaceSummary[];
}

export interface DemoStatusResponse {
  workspaceId: string;
  isDemo: boolean;
}

export interface DeleteDemoWorkspaceResponse {
  message: string;
  workspaceId: string;
}

// ============================================================
// API Definition
// ============================================================

export const demoApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Create a demo workspace for the authenticated user.
     * Idempotent: returns existing demo workspace if one exists (unless forceNew=true).
     */
    createDemoWorkspace: builder.mutation<CreateDemoWorkspaceResponse, CreateDemoWorkspaceRequest | void>({
      query: (body) => ({
        url: '/demo/workspace',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [
        { type: 'Demo', id: 'LIST' },
        { type: 'Workspace', id: 'LIST' },
      ],
    }),

    /**
     * List all demo workspaces for the authenticated user.
     */
    listDemoWorkspaces: builder.query<ListDemoWorkspacesResponse, void>({
      query: () => '/demo/workspaces',
      providesTags: [{ type: 'Demo', id: 'LIST' }],
    }),

    /**
     * Check if a workspace is a demo workspace.
     */
    checkDemoStatus: builder.query<DemoStatusResponse, string>({
      query: (workspaceId) => `/demo/workspace/${workspaceId}/status`,
      providesTags: (result, error, workspaceId) => [
        { type: 'Demo', id: workspaceId },
      ],
    }),

    /**
     * Delete a demo workspace.
     */
    deleteDemoWorkspace: builder.mutation<DeleteDemoWorkspaceResponse, string>({
      query: (workspaceId) => ({
        url: `/demo/workspace/${workspaceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Demo', id: 'LIST' },
        { type: 'Workspace', id: 'LIST' },
      ],
    }),
  }),
});

// ============================================================
// Export Hooks
// ============================================================

export const {
  useCreateDemoWorkspaceMutation,
  useListDemoWorkspacesQuery,
  useCheckDemoStatusQuery,
  useDeleteDemoWorkspaceMutation,
} = demoApi;
